import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Botao, Cartao, CartaoTopo, Selo, Medidor, Vazio, Campo, Entrada } from "@notes/ui";
import { KeyRound, ArrowLeft, Webhook, Copy, Trash2, ShieldCheck, ExternalLink, Gauge } from "lucide-react";
import { Pagina, Secao, Erro } from "../shell/Shell.js";
import { trpc, mensagemDeErro } from "../lib/api.js";

/**
 * CHAVES, WEBHOOKS E PLANO.
 *
 * Segredo aparece uma vez e a tela avisa ANTES. Depois disso não existe jeito
 * de recuperar, nem para quem administra a agência: guardar o valor para poder
 * mostrar de novo é o mesmo que guardar senha em texto.
 */
export function Integracoes() {
  const qc = useQueryClient();
  const [nome, setNome] = useState("");
  const [escolhidos, setEscolhidos] = useState<string[]>(["reunioes:ler"]);
  const [segredoNovo, setSegredoNovo] = useState<{ oQue: string; valor: string } | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [url, setUrl] = useState("https://");

  const chaves = useQuery({ queryKey: ["integracoes", "chaves"], queryFn: () => trpc.integracoes.chaves.query() });
  const webhooks = useQuery({ queryKey: ["integracoes", "webhooks"], queryFn: () => trpc.integracoes.webhooks.query() });
  const plano = useQuery({ queryKey: ["integracoes", "plano"], queryFn: () => trpc.integracoes.plano.query() });

  const recarregar = () => void qc.invalidateQueries({ queryKey: ["integracoes"] });

  const criarChave = useMutation({
    mutationFn: () => trpc.integracoes.criarChave.mutate({ nome, escopos: escolhidos as never }),
    onSuccess: (r) => { setSegredoNovo({ oQue: "Chave da API", valor: r.chave }); setCopiado(false); setNome(""); recarregar(); },
  });
  const revogarChave = useMutation({
    mutationFn: (id: string) => trpc.integracoes.revogarChave.mutate({ id }), onSuccess: recarregar,
  });
  const criarWebhook = useMutation({
    mutationFn: () => trpc.integracoes.criarWebhook.mutate({ url, eventos: [] }),
    onSuccess: (r) => {
      if (!r.ok) return;
      setSegredoNovo({ oQue: "Segredo do webhook", valor: r.segredo }); setCopiado(false);
      setUrl("https://"); recarregar();
    },
  });
  const apagarWebhook = useMutation({
    mutationFn: (id: string) => trpc.integracoes.apagarWebhook.mutate({ id }), onSuccess: recarregar,
  });

  const p = plano.data?.plano;

  return (
    <Pagina
      titulo="Chaves, webhooks e plano"
      descricao="Acesso de outros sistemas ao acervo, avisos assinados, e o consumo do ciclo."
      acao={<Link to="/configuracoes"><Botao tamanho="sm" variante="fantasma"><ArrowLeft size={14} />Configurações</Botao></Link>}
    >
      {segredoNovo && (
        <Secao ordem={0}>
          <Cartao className="enotes-segredo">
            <CartaoTopo icone={<ShieldCheck size={15} />} titulo={`${segredoNovo.oQue}, uma vez só`}
              descricao="Guarde agora. Depois de fechar, não tem como mostrar de novo." />
            <code className="enotes-codigo">{segredoNovo.valor}</code>
            <div className="enotes-acoes">
              <Botao variante="primario" onClick={() => { void navigator.clipboard.writeText(segredoNovo.valor); setCopiado(true); }}>
                <Copy size={14} /> {copiado ? "Copiado" : "Copiar"}
              </Botao>
              <Botao variante="fantasma" onClick={() => setSegredoNovo(null)}>Já guardei</Botao>
            </div>
          </Cartao>
        </Secao>
      )}

      {p && (
        <Secao titulo="Seu plano" ordem={1}>
          <Cartao>
            <CartaoTopo
              icone={<Gauge size={15} />}
              titulo={<>{p.nome} <Selo tom={p.status === "ativa" ? "bom" : "atencao"}>{p.status}</Selo></>}
              descricao={`${p.assentos} ${p.assentos === 1 ? "assento" : "assentos"} · ${Math.round(p.minutosUsados / 60)} de ${Math.round(p.minutosIncluidos / 60)} horas usadas neste ciclo`}
            />
            <Medidor valor={p.porcentagem} rotulo="Horas do ciclo" tom={p.porcentagem > 90 ? "critico" : p.porcentagem > 70 ? "atencao" : "marca"} />
            {p.minutosExcedentes > 0 && (
              <p className="enotes-aviso">
                <span aria-hidden className="sinal sinal-atencao" />
                {Math.round(p.minutosExcedentes / 60)} horas acima do incluso neste ciclo.
              </p>
            )}
          </Cartao>
        </Secao>
      )}

      <Secao titulo="Chaves da API" ordem={2}>
        <Cartao>
          <CartaoTopo
            icone={<KeyRound size={15} />} titulo="Acesso de outro sistema ao acervo"
            descricao={<>A referência das rotas, dos webhooks e do conector está em <a className="enotes-linha-link" href="/docs" target="_blank" rel="noreferrer">/docs <ExternalLink size={11} /></a>.</>}
          />
          <form className="enotes-form" onSubmit={(e) => { e.preventDefault(); if (nome.trim().length >= 2) criarChave.mutate(); }}>
            <Campo rotulo="Para que serve esta chave" htmlFor="nome-chave"
              erro={criarChave.isError ? mensagemDeErro(criarChave.error) : null}>
              <Entrada id="nome-chave" value={nome} onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: painel do BI da agência" aria-label="Nome da chave" />
            </Campo>
            <div className="enotes-chips">
              {(chaves.data?.escoposDisponiveis ?? []).map((e) => (
                <button key={e} type="button" className="enotes-chip" aria-pressed={escolhidos.includes(e)}
                  data-ativo={escolhidos.includes(e) ? "sim" : undefined}
                  onClick={() => setEscolhidos((a) => a.includes(e) ? a.filter((x) => x !== e) : [...a, e])}>
                  {e}
                </button>
              ))}
            </div>
            <div><Botao variante="primario" ocupado={criarChave.isPending}>Criar chave</Botao></div>
          </form>

          {(chaves.data?.chaves.length ?? 0) > 0 ? (
            <ul className="enotes-itens" style={{ marginTop: "1rem" }}>
              {chaves.data!.chaves.map((c) => (
                <li key={c.id}>
                  <KeyRound size={15} aria-hidden />
                  <span className="enotes-item-texto">{c.nome}</span>
                  <span className="carimbo num">{c.prefixo}</span>
                  <span className="carimbo">{c.escopos.length} escopos</span>
                  <Botao tamanho="sm" variante="fantasma" onClick={() => revogarChave.mutate(c.id)}>
                    <Trash2 size={13} /> Revogar
                  </Botao>
                </li>
              ))}
            </ul>
          ) : chaves.isSuccess ? (
            <Vazio titulo="Nenhuma chave" descricao="Crie uma para outro sistema ler o acervo." icone={<KeyRound size={24} />} />
          ) : null}
        </Cartao>
      </Secao>

      <Secao titulo="Webhooks" ordem={3}>
        <Cartao>
          <CartaoTopo icone={<Webhook size={15} />} titulo="Avisos assinados para o seu sistema"
            descricao="Avisamos quando uma reunião termina, quando uma promessa vence e quando uma ação é aprovada." />
          <form className="enotes-form" onSubmit={(e) => { e.preventDefault(); criarWebhook.mutate(); }}>
            <Campo rotulo="Endereço do webhook" htmlFor="url-webhook"
              erro={criarWebhook.data && !criarWebhook.data.ok
                ? criarWebhook.data.motivo === "endereco_privado"
                  ? "Esse endereço aponta para dentro de uma rede privada. Use um endereço que a internet alcança."
                  : criarWebhook.data.motivo === "dns_nao_resolveu"
                    ? "Não consegui encontrar esse endereço. Confira o domínio."
                    : "Use um endereço https."
                : criarWebhook.isError ? mensagemDeErro(criarWebhook.error) : null}>
              <Entrada id="url-webhook" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://seusistema.com/notes" aria-label="Endereço do webhook" />
            </Campo>
            <div><Botao variante="primario" ocupado={criarWebhook.isPending}>Adicionar</Botao></div>
          </form>

          {(webhooks.data?.endpoints.length ?? 0) > 0 ? (
            <ul className="enotes-itens" style={{ marginTop: "1rem" }}>
              {webhooks.data!.endpoints.map((w) => (
                <li key={w.id}>
                  <Webhook size={15} aria-hidden />
                  <span className="enotes-item-texto">{w.url}</span>
                  {!w.ativo && <Selo tom="critico">desligado</Selo>}
                  {w.falhasSeguidas > 0 && <Selo tom="atencao">{w.falhasSeguidas} falhas seguidas</Selo>}
                  <Botao tamanho="sm" variante="fantasma" onClick={() => apagarWebhook.mutate(w.id)}>
                    <Trash2 size={13} /> Remover
                  </Botao>
                </li>
              ))}
            </ul>
          ) : webhooks.isSuccess ? (
            <Vazio titulo="Nenhum webhook" descricao="Adicione um endereço para receber os avisos." icone={<Webhook size={24} />} />
          ) : null}
        </Cartao>
      </Secao>
    </Pagina>
  );
}
